import { FolderKind } from '@mimir/common/dto/folderModels';
import type { LayoutFeatures } from './model/layoutFeatures';

const defaultPathFeatures: LayoutFeatures = {
  rich: true
};

const exportLayoutFeatures: LayoutFeatures = {
  export: true
};

const browseLayoutFeatures: LayoutFeatures = {
  browse: true
};

const adminNav = {
  title: 'Admin',
  links: [
    {
      text: 'Dashboard',
      to: { name: 'admin' }
    },
    {
      text: 'System Tasks',
      to: { name: 'adminSystemTasks' }
    }
  ]
};

const routes = [
  {
    path: '/',
    component: () => import('./pages/layout/Layout.vue'),
    props: {
      features: defaultPathFeatures,
    },
    children: [
      {
        path: '/',
        alias: '/search-item',
        name: 'searchItem',
        component: () => import('./pages/search/SearchItem.vue'),
      },
      {
        path: '/cutter',
        name: 'cutter',
        component: () => import('./pages/search/SearchItem.vue'),
      },
      {
        path: '/index.html',
        redirect: { name: 'searchItem' },
      },
      {
        path: '/search-person',
        redirect: { name: 'searchItem' }, // For old bookmarks containing search-person
      },
      {
        path: '/share',
        name: 'share',
        component: () => import('@mimir/web/pages/share/MyShares.vue'),
      },
      {
        path: '/userTasks',
        name: 'userTasks',
        component: () => import('@mimir/web/pages/tasks/MyTasks.vue'),
      },
      {
        path: '/activity',
        name: 'activity',
        component: () => import('@mimir/web/pages/activity/ActivityMonitor.vue'),
      },
      {
        path: '/scheduler',
        name: 'scheduler',
        component: () => import('@mimir/web/pages/scheduler/SchedulePage.vue'),
      },
      {
        path: '/playout',
        name: 'playout',
        component: () => import('@mimir/web/pages/playout/PlayoutPage.vue'),
      },
      {
        path: '/premiere',
        name: 'premiere',
        component: () => import('@mimir/web/pages/premiere/ProjectSyncing.vue'),
      },
      {
        path: '/settings*',
        name: 'settings',
        component: () => import('@mimir/web/pages/settings/Settings.vue'),
      },
      {
        path: '/my-account',
        redirect: { name: 'myOrganization'}, // For old bookmarks containing my-account
      },
      {
        path: '/my-organisation',
        name: 'myOrganization',
        component: () => import('@mimir/web/pages/user/MyOrganization.vue'),
      },
      {
        path: '/changelog',
        name: 'changelog',
        component: () => import('@mimir/web/pages/user/Changelog.vue'),
        props: () => ({
          staticHeightInPx: 52 + 32 + 52, // header, top padding, settings header
        }),
      },
      {
        path: '/user-settings',
        name: 'userSettings',
        component: () => import('@mimir/web/pages/user/UserSettings.vue'),
      },
      {
        path: '/desktop-settings',
        name: 'desktopSettings',
        component: () => import('@mimir/web/pages/user/DesktopSettings.vue'),
      },
      {
        path: '/ame-settings',
        name: 'ameSettings',
        component: () => import('@mimir/web/pages/user/AmeSettings.vue'),
      },
      {
        path: '/dvr-settings',
        name: 'dvrSettings',
        component: () => import('@mimir/web/pages/user/DvrSettings.vue'),
      },
      {
        path: '/logging-settings',
        name: 'loggingSettings',
        component: () => import('@mimir/web/pages/user/LoggingSettings.vue'),
      },
      {
        path: '/item-details/:id',
        name: 'itemDetails',
        component: () => import('@mimir/web/pages/item/ItemDetails.vue'),
        // @ts-expect-error types differ between vue-router and vue-router-next
        props: route => ({
          itemId: route.params.id,
        }),
      },
      {
        path: '/person-details/:id',
        name: 'personDetails',
        component: () => import('@mimir/web/pages/person/PersonDetails.vue'),
      },
      {
        path: '/admin',
        name: 'admin',
        component: () => import('@mimir/web/pages/admin/Overview.vue'),
        meta: {
          breadcrumbs: [
            {
              text: 'Admin',
              disabled: true,
            },
            {
              text: 'Dashboard',
              disabled: true,
            }
          ],
          nav: adminNav,
        }
      },
      {
        path: '/admin/systemTasks',
        name: 'adminSystemTasks',
        component: () => import('@mimir/web/pages/admin/SystemTaskList.vue'),
        meta: {
          breadcrumbs: [
            {
              text: 'Admin',
              disabled: true,
            },
            {
              text: 'System Tasks',
              disabled: true,
            }
          ],
          basicLayout: true,
          nav: adminNav,
        }
      },
      {
        path: '/admin/systemTasks/start',
        name: 'adminSystemTasksStart',
        component: () => import('@mimir/web/pages/admin/StartSystemTask.vue'),
        meta: {
          breadcrumbs: [
            {
              text: 'Admin',
              disabled: true,
            },
            {
              text: 'System Tasks',
              name: 'adminSystemTasksStart',
              disabled: false,
              to: { name: 'adminSystemTasks' },
              exact: true
            },
            {
              text: 'Start',
              disabled: true,
            }
          ],
          basicLayout: true,
          nav: adminNav,
        }
      },
      {
        path: '/support',
        name: 'support',
        component: () => import('@mimir/web/pages/support/SupportDashboard.vue'),
      },
      {
        path: 'folders',
        name: 'folderView',
        component: () => import('./pages/search/SearchItem.vue'),
        // @ts-expect-error types differ between vue-router and vue-router-next
        props: route => ({
          folderId: route.query.folderId,
        }),
        // @ts-expect-error types differ between vue-router and vue-router-next
        beforeEnter: (to, _from, next) => {
          if (to.query.folderId) {
            next();
            return;
          }

          const defaultQuery = { folderId: FolderKind.TenantRoot };
          next({
            name: 'folderView',
            query: { ...to.query, ...defaultQuery },
          });
        },
      },
      {
        path: '/recycle-bin',
        name: 'recycleBin',
        component: () => import('@mimir/web/pages/search/SearchItem.vue'),
        children: [],
      },
      {
        path: '/change-password',
        name: 'changePassword',
        component: () => import('@mimir/web/pages/user/ChangePassword.vue'),
        children: [],
      },
    ],
  },
  {
    path: '/export',
    name: 'export',
    component: () => import('./pages/layout/Layout.vue'),
    props: {
      features: exportLayoutFeatures
    }
  },
  {
    path: '/browse',
    name: 'browse',
    component: () => import('./pages/layout/Layout.vue'),
    props: {
      features: browseLayoutFeatures
    },
  },
  {
    path: '/mos',
    name: 'mos',
    component: () => import('./pages/mos/MosHostExampleLayout.vue'),
  },
  {
    path: '/drag',
    name: 'drag',
    component: () => import('./pages/layout/DragTest.vue'),
  },
  {
    path: '/framedrag',
    name: 'framedrag',
    component: () => import('./pages/layout/FrameDragTest.vue'),
  },
  {
    path: '/public',
    name: 'public',
    component: () => import('@mimir/web/pages/layout/Blank.vue'),
    children: [
      {
        path: 'forgot-password',
        name: 'forgotPassword',
        component: () => import('@mimir/web/pages/public/ForgotPassword.vue'),
      },
      {
        path: 'forgot-password/verify',
        name: 'forgotPasswordVerify',
        component: () => import('@mimir/web/pages/public/ForgotPassword.vue'),
        props: {
          verify: true
        }
      },
      {
        path: 'forgot-password/reset',
        name: 'forgotPasswordReset',
        component: () => import('@mimir/web/pages/public/ForgotPassword.vue'),
        props: {
          afterReset: {
            path: '/'
          }
        }
      }
    ],
  },
  {
    path: '/dev',
    name: 'bareDev',
    component: () => import('@mimir/web/pages/bare/BareDev.vue'),
  },
  {
    path: '/sign-in-code',
    name: 'signInCode',
    component: () => import('@mimir/web/pages/bare/SignInCode.vue'),
    // @ts-expect-error types differ between vue-router and vue-router-next
    props: route => ({ code: route.query.code })
  }
];

export default routes;
